/**
 * EntityInlineDetail — Inline entity detail panel (no Drawer chrome)
 *
 * Used in master-detail mode as the right panel content.
 * Renders the same key-value detail view as EntityDetailPanel's Details tab,
 * reusing formatFieldName + FieldValue from RecordDetailDrawer (DRY).
 *
 * For full detail with tabs, the user clicks "Expand" to open EntityDetailPanel.
 */

import {
  Stack,
  Table,
  Text,
  ScrollArea,
  ActionIcon,
  Group,
  Tooltip,
  Divider,
} from '@mantine/core';
import { IconArrowsMaximize, IconX } from '@tabler/icons-react';
import { formatFieldName, FieldValue, INTERNAL_ID_FIELDS } from '@/components/RecordDetailDrawer';
import { EntityMediaHeader } from './EntityMediaHeader';
import type { PrimitiveItem } from '@/hooks/usePrimitiveData';
import { getPrimitiveMetadata, type PrimitiveType } from '@/config/primitive-metadata';

const STATUS_COLOR_MAP: Record<string, string> = {
  ACTIVE: 'green',
  PENDING: 'yellow',
  COMPLETED: 'green',
  FAILED: 'red',
  ARCHIVED: 'gray',
  DRAFT: 'blue',
  PAUSED: 'gray',
  IN_PROGRESS: 'blue',
};

interface EntityInlineDetailProps {
  entity: PrimitiveItem;
  primitiveType: PrimitiveType;
  onClose?: () => void;
  onExpand?: () => void;
}

export function EntityInlineDetail({
  entity,
  primitiveType,
  onClose,
  onExpand,
}: EntityInlineDetailProps) {
  const metadata = getPrimitiveMetadata(primitiveType);
  const PrimitiveIcon = metadata.icon as React.ComponentType<{ size: number }>;
  const entityName = (entity.name as string) ?? metadata.name;

  return (
    <Stack gap={0} h="100%">
      {/* Header with media + close/expand buttons */}
      <Group justify="space-between" p="sm" style={{ borderBottom: '1px solid var(--mantine-color-default-border)' }}>
        <EntityMediaHeader
          name={entityName}
          subtitle={entity.description as string | undefined}
          status={entity.status as string | undefined}
          statusColorMap={STATUS_COLOR_MAP}
          safetyLevel={entity.safetyLevel as string | undefined}
          imageUrl={entity.profileImageUrl as string | undefined}
          accentColor={metadata.phaseColor}
          Icon={PrimitiveIcon}
          compact
        />
        <Group gap={4}>
          {onExpand && (
            <Tooltip label="Open full detail panel">
              <ActionIcon variant="subtle" size="sm" onClick={onExpand}>
                <IconArrowsMaximize size={14} />
              </ActionIcon>
            </Tooltip>
          )}
          {onClose && (
            <Tooltip label="Close detail">
              <ActionIcon variant="subtle" size="sm" onClick={onClose} c="gray">
                <IconX size={14} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Group>

      <Divider />

      {/* Detail content — key-value table matching EntityDetailPanel Details tab */}
      <ScrollArea style={{ flex: 1 }} p="sm" offsetScrollbars>
        <Table striped highlightOnHover>
          <Table.Tbody>
            {Object.entries(entity)
              .filter(([key]) => !key.startsWith('_') && key !== '__typename' && !INTERNAL_ID_FIELDS.has(key))
              .map(([key, value]) => (
                <Table.Tr key={key}>
                  <Table.Td w={160}>
                    <Text size="xs" fw={500} c="dimmed">
                      {formatFieldName(key)}
                    </Text>
                  </Table.Td>
                  <Table.Td>
                    <FieldValue value={value} fieldKey={key} entity={entity as Record<string, unknown>} />
                  </Table.Td>
                </Table.Tr>
              ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Stack>
  );
}
